import { useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { usePushNotifications, isPushDisabledByUser } from '@/hooks/usePushNotifications';
import { WEB_PUSH_TOKEN_OWNER_KEY, releaseWebPushDevice, setPushIdentity } from '@/lib/api/services/pushNotifications';
import { useAppBadge } from '@/hooks/useAppBadge';

const readTokenOwner = (): string | null => {
  try {
    return localStorage.getItem(WEB_PUSH_TOKEN_OWNER_KEY);
  } catch {
    return null;
  }
};

export function PushNotificationManager() {
  const { user } = useAuth();
  const { permission, token, requestPermission, isSupported } = usePushNotifications();

  useAppBadge();

  // Tell the push service worker who is signed in, so a background push
  // addressed to anyone else is dropped even while no tab is open. Only a
  // REAL user is written: `user` is briefly null while auth restores, and
  // sign-out records the signed-out state itself (releaseWebPushDevice).
  useEffect(() => {
    if (user?.id) void setPushIdentity(user.id);
  }, [user?.id]);

  useEffect(() => {
    if (!user || !isSupported || permission !== 'granted') return;

    if (isPushDisabledByUser(user.id)) {
      // This user turned push off, but the browser's token may still be bound
      // to the PREVIOUS user on this browser — whose pushes would keep landing
      // here. Detach it (the backend releases a token to whoever holds it).
      if (token && readTokenOwner() !== user.id) {
        void releaseWebPushDevice(3000, { signedOut: false });
      }
      return;
    }

    if (!token && sessionStorage.getItem('fcm_auto_register_attempted') !== 'true') {
      sessionStorage.setItem('fcm_auto_register_attempted', 'true');
      requestPermission({ silent: true });
      return;
    }

    // The stored token was registered for someone else (a previous user on this
    // browser, whose session ended without a sign-out). Re-register so the
    // backend moves the token to the current user; otherwise this browser keeps
    // receiving the previous user's pushes.
    const rebindKey = `fcm_rebind_attempted_${user.id}`;
    if (token && readTokenOwner() !== user.id && sessionStorage.getItem(rebindKey) !== 'true') {
      sessionStorage.setItem(rebindKey, 'true');
      requestPermission({ silent: true });
    }
  }, [user, isSupported, permission, token, requestPermission]);

  return null;
}
